import { RefObject, useCallback, useRef } from "react";
import { MenuItem, MenuItemProps } from "@mui/material";
import { Link } from "react-router-dom";
import SvgIcon from "../../helpers/SvgIcon";
import LoadingIndicator from "../misc/LoadingIndicator";
import { ActionMenuItem, ActionMenuProps } from "./actionMenu.interface";
import ActionMenuTooltip from "./ActionMenuTooltip";
import ActionMenuKindIndicator from "./ActionMenuKindIndicator";
import { actionMenuKindIcons } from "./actionMenuKindConfig";

interface ActionMenuOptionProps {
  item: ActionMenuItem;
  menuRef: RefObject<HTMLElement>;
  tooltipSize: ActionMenuProps["tooltipSize"];
  tooltipOpen: boolean;
  onTooltipOpen: () => void;
  onTooltipClose: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  tabIndex?: number;
  onSelect: (item: ActionMenuItem) => void;
}

function ActionMenuOption({
  item,
  menuRef,
  tooltipSize,
  tooltipOpen,
  onTooltipOpen,
  onTooltipClose,
  disabled,
  autoFocus,
  tabIndex,
  onSelect,
}: ActionMenuOptionProps) {
  const itemRef = useRef<HTMLElement | null>(null);
  const setItemRef = useCallback((element: HTMLElement | null) => {
    itemRef.current = element;
  }, []);
  const leadingIcon =
    item.kind && item.icon === actionMenuKindIcons[item.kind]
      ? "DOCUMENTS"
      : item.icon;
  const content = (
    <>
      {(leadingIcon || item.loading) && (
        <span className="mr-3 inline-flex h-5 w-5 shrink-0 items-center justify-center [&_svg]:!h-5 [&_svg]:!w-5">
          {item.loading ? (
            <LoadingIndicator size={18} color="inherit" />
          ) : (
            leadingIcon && <SvgIcon type={leadingIcon} fill="currentColor" />
          )}
        </span>
      )}
      <span className="min-w-0 flex-1">{item.label}</span>
      <ActionMenuKindIndicator kind={item.kind} />
    </>
  );
  const menuItemProps: MenuItemProps = {
    disabled,
    "aria-busy": item.loading || undefined,
    autoFocus,
    tabIndex,
    onFocus: onTooltipOpen,
    onBlur: onTooltipClose,
    onClick: (event) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      onSelect(item);
    },
    sx: {
      minHeight: 42,
      borderRadius: "6px",
      px: 1.5,
      py: 1,
      gap: 0,
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
      whiteSpace: "normal",
      color:
        !item.color || item.color === "default"
          ? "text.primary"
          : item.color === "inherit"
          ? "inherit"
          : `${item.color}.main`,
      "&:hover, &.Mui-focusVisible": { backgroundColor: "#F1F5F9" },
      // Pastreaza hover/focus pentru explicarea actiunilor dezactivate.
      "&.Mui-disabled": { pointerEvents: "auto", opacity: 0.45 },
    },
  };
  const canNavigate = item.navigate && !item.withConfirmationModal && !disabled;

  return (
    <ActionMenuTooltip
      title={item.tooltip}
      menuRef={menuRef}
      itemRef={itemRef}
      size={tooltipSize}
      open={tooltipOpen}
      onOpen={onTooltipOpen}
      onClose={onTooltipClose}>
      {canNavigate ? (
        <MenuItem
          component={Link}
          to={item.navigate!}
          {...menuItemProps}
          ref={setItemRef}>
          {content}
        </MenuItem>
      ) : (
        <MenuItem {...menuItemProps} ref={setItemRef}>
          {content}
        </MenuItem>
      )}
    </ActionMenuTooltip>
  );
}

export default ActionMenuOption;
